import axiosInstance from 'config/axiosInstance';
import { discardResendRecord, readResendRecord, writeResendRecord } from 'config/createRecordStore';
import { CreateOutcome, REQUEST_ID_REUSE_MS, submitIdempotentCreate } from 'config/idempotentCreate';
import { getSessionIdentity } from 'config/session';
import { newRequestId } from 'components/Driver/registerDriver';

/**
 * YAC-2026-009 PR5b。運営の画面が呼ぶ API（/v1/operator/...。#383）を、このファイルにまとめる。
 * 利用側の契約（services/backend/api/contracts/admin-flow-consumers.json）の sources に入れ、送る項目・
 * 読む項目を OpenAPI と照らす。画面の部品はここの関数だけを使い、API を直に呼ばない。
 *
 * - 作成と招待（会社の登録・管理者の招待・運営の招待）は X-Request-ID を付ける。結果が分からない失敗の
 *   後も二重に作らないよう、既存の作成と同じ仕組み（config/idempotentCreate の submitIdempotentCreate）を使う。
 * - 管理者の招待の再送も X-Request-ID を付ける。運転者の再送（ResendInvitation）と同じく、結果が分からない
 *   失敗の後にもう一度押したら同じ値で送る（新しい初期パスワードを作り直させない）。
 */

export const OPERATOR_PAGE_SIZE = 10;

export type AccountStatus = 'active' | 'invited' | 'invitation_expired' | 'inactive';
export type AccountRole = 'driver' | 'manager' | 'operator';

export interface OperatorCompany {
  id: number;
  name: string;
  prefecture?: string;
  postal_code?: string;
  city?: string;
  street?: string;
  phone?: string;
  memo?: string;
  time_zone?: string;
  manager_count: number;
  create_time: string;
  update_time: string;
}

export interface Account {
  id: number;
  email: string;
  name: string;
  roles: AccountRole[];
  status: AccountStatus;
  is_active: boolean;
  invitation_expires_at: string | null;
  create_time: string;
}

export interface OperatorAccount extends Account {
  /** 最後の運営の保護で、有効な運営として数えるか。 */
  counted: boolean;
}

export interface InvitedManager extends Account {
  result: 'invited' | 'role_added';
}

export interface Page<T> {
  count: number;
  results: T[];
}

export interface TimeZoneOption {
  name: string;
  utc_offset: string;
}

/** 会社の項目（登録と変更で送るもの）。 */
export const COMPANY_FIELDS = [
  'name',
  'postal_code',
  'prefecture',
  'city',
  'street',
  'phone',
  'time_zone',
  'memo',
] as const;
export type CompanyField = (typeof COMPANY_FIELDS)[number];
export type CompanyValues = Record<CompanyField, string>;

export interface Invitation {
  email: string;
  name: string;
}

const COMPANIES_URL = '/v1/operator/companies/';
const OPERATORS_URL = '/v1/operator/operators/';
const companyUrl = (id: number | string) => `/v1/operator/companies/${id}/`;
const managersUrl = (companyId: number | string) => `/v1/operator/companies/${companyId}/managers/`;
const managerUrl = (companyId: number | string, id: number) =>
  `/v1/operator/companies/${companyId}/managers/${id}/`;

const listQuery = (page: number, search: string) =>
  `?page=${page}&page_size=${OPERATOR_PAGE_SIZE}&search=${encodeURIComponent(search)}`;

const asPage = <T>(data: any): Page<T> => ({
  count: typeof data?.count === 'number' ? data.count : 0,
  results: Array.isArray(data?.results) ? data.results : [],
});

/** 会社の一覧（名前で検索・1ページ10件）。 */
export async function listCompanies(page: number, search: string): Promise<Page<OperatorCompany>> {
  const response = await axiosInstance.get(`/v1/operator/companies/${listQuery(page, search)}`);
  return asPage<OperatorCompany>(response.data);
}

export async function getCompany(id: number | string): Promise<OperatorCompany> {
  const response = await axiosInstance.get(companyUrl(id));
  return response.data;
}

/**
 * 会社の登録。manager を渡すと、最初の管理者を一緒に招待する（サーバーは1つのトランザクションで作り、
 * 招待のメールを送れなければ会社も取り消して 502 email_not_sent を返す）。
 */
export function createCompany(values: CompanyValues, manager: Invitation | null): Promise<CreateOutcome> {
  const payload: Record<string, unknown> = { ...values };
  if (manager) payload.manager = { email: manager.email, name: manager.name };
  return submitIdempotentCreate('operatorCompany', COMPANIES_URL, payload);
}

/** 会社の変更（変えた項目だけを送る）。 */
export async function updateCompany(
  id: number | string,
  changes: Partial<CompanyValues>
): Promise<OperatorCompany> {
  const response = await axiosInstance.patch(companyUrl(id), changes);
  return response.data;
}

/** 会社の時間帯として選べる名前（会社の設定の画面と同じ一覧）。 */
export async function listTimeZones(): Promise<TimeZoneOption[]> {
  const response = await axiosInstance.get('/v1/company/time_zones/');
  return Array.isArray(response.data) ? response.data : [];
}

export async function listManagers(
  companyId: number | string,
  page: number,
  search: string
): Promise<Page<Account>> {
  const response = await axiosInstance.get(
    `/v1/operator/companies/${companyId}/managers/${listQuery(page, search)}`
  );
  return asPage<Account>(response.data);
}

/**
 * 管理者の招待。新しいアカウントなら 201・result invited、同じ会社のアカウントに管理者の役割を足したら
 * 200・result role_added。宛先（会社）ごとに別の操作として X-Request-ID を使い分ける（scope）。
 */
export function inviteManager(companyId: number | string, invitation: Invitation): Promise<CreateOutcome> {
  return submitIdempotentCreate(
    'operatorManager',
    managersUrl(companyId),
    { email: invitation.email, name: invitation.name },
    `company:${companyId}`
  );
}

/**
 * 招待の再送で、結果が分からない失敗か（同じ X-Request-ID で送り直す）。運転者の再送（ResendInvitation の
 * keepsRequestId）と同じ考え。応答が無い・5xx（理由の決まった 502 email_not_sent を除く）・408・429・
 * 401・403・409 idempotency_in_progress は、前の要求が確定したか分からない。
 */
const keepsRequestId = (error: any): boolean => {
  const response = error?.response;
  if (!response) return true;
  const { status } = response;
  if (status === 502 && response.data?.code === 'email_not_sent') return false;
  if (status >= 500 || status === 408 || status === 429 || status === 401 || status === 403) return true;
  return status === 409 && response.data?.code === 'idempotency_in_progress';
};

const resendKey = (companyId: number | string, id: number) => `operator-manager:${companyId}:${id}`;

/** 管理者の招待の再送（初期パスワードを作り直してメールを送り直す）。 */
export async function resendManagerInvitation(companyId: number | string, id: number): Promise<Account> {
  const key = resendKey(companyId, id);
  const held = readResendRecord(key);
  const reusable =
    held &&
    typeof held.requestId === 'string' &&
    typeof held.savedAt === 'number' &&
    held.session === getSessionIdentity() &&
    Date.now() >= held.savedAt &&
    Date.now() - held.savedAt < REQUEST_ID_REUSE_MS;
  const requestId: string = reusable ? held.requestId : newRequestId();
  if (!reusable) writeResendRecord(key, { requestId, session: getSessionIdentity(), savedAt: Date.now() });
  const discard = () => {
    if (readResendRecord(key)?.requestId === requestId) discardResendRecord(key);
  };
  try {
    const response = await axiosInstance.post(`${managerUrl(companyId, id)}resend_invitation/`, undefined, {
      headers: { 'X-Request-ID': requestId },
    });
    discard();
    return response.data;
  } catch (error) {
    if (!keepsRequestId(error)) discard();
    throw error;
  }
}

export async function deactivateManager(companyId: number | string, id: number): Promise<Account> {
  const response = await axiosInstance.post(`${managerUrl(companyId, id)}deactivate/`);
  return response.data;
}

export async function reactivateManager(companyId: number | string, id: number): Promise<Account> {
  const response = await axiosInstance.post(`${managerUrl(companyId, id)}reactivate/`);
  return response.data;
}

/** 管理者の役割を外す。ほかの役割があれば role_removed、管理者だけなら deleted（論理削除）。 */
export async function removeManager(companyId: number | string, id: number): Promise<'role_removed' | 'deleted'> {
  const response = await axiosInstance.delete(managerUrl(companyId, id));
  return response.data?.result;
}

export async function listOperators(page: number, search: string): Promise<Page<OperatorAccount>> {
  const response = await axiosInstance.get(`/v1/operator/operators/${listQuery(page, search)}`);
  return asPage<OperatorAccount>(response.data);
}

export function inviteOperator(invitation: Invitation): Promise<CreateOutcome> {
  return submitIdempotentCreate('operatorAccount', OPERATORS_URL, {
    email: invitation.email,
    name: invitation.name,
  });
}

/** 運営の役割を外す。最後の有効な運営なら 409 last_operator。 */
export async function removeOperator(id: number): Promise<'role_removed' | 'deleted'> {
  const response = await axiosInstance.delete(`/v1/operator/operators/${id}/`);
  return response.data?.result;
}

/** 今のログインの利用者の id（セッションの印「sid:user_id」から）。分からなければ null。 */
export function currentUserId(): number | null {
  const identity = getSessionIdentity();
  if (!identity) return null;
  const value = Number(identity.slice(identity.indexOf(':') + 1));
  return Number.isInteger(value) && value > 0 ? value : null;
}
